/* eslint-disable react/jsx-props-no-spreading */
import { useSpring, animated, easings } from '@react-spring/web';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useState, useEffect, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';

import * as AppPropTypes from '../../lib/PropTypes';
import { getImageAtSize } from '../../lib/utils';

import { useTheme, useThemeClassName } from '../../contexts/ThemeContext';
import Button from '../buttons/Button';
import { WheelIndicator } from '../icons';
import CurvedArrow from '../icons/CurvedArrow';

import styles from '../../../styles/questions/wheel.module.scss';

const propTypes = {
    question: AppPropTypes.question,
    answer: AppPropTypes.participationAnswer,
    onAnswer: PropTypes.func,
    onAnimationEnd: PropTypes.func,
    className: PropTypes.string,
};

const defaultProps = {
    question: null,
    answer: null,
    onAnswer: null,
    onAnimationEnd: null,
    className: null,
};

function WheelQuestion({ question, answer, onAnswer, onAnimationEnd, className }) {
    const { name: theme } = useTheme();
    const themeClassName = useThemeClassName(styles);

    const { image = null, logo = null, choices = [], body = null, id = null } = question || {};

    // console.log('question', question);

    const answerIndex =
        answer !== null
            ? choices.findIndex(({ id: choiceId = null }) => choiceId === answer.value)
            : null;
    const sliceDegree = 360 / choices.length;
    const center = sliceDegree * 0.5;
    const answerDegree = answerIndex !== null ? 360 - (sliceDegree * answerIndex + center) : null;

    const [spinTo, setSpinTo] = useState(answerDegree !== null ? answerDegree + 1080 : 0);
    const [hasAnswer] = useState(answerDegree !== null);

    const disabled = spinTo !== 0 || answer !== null;

    const spring = useSpring({
        from: {
            transform: hasAnswer ? `rotate(${spinTo}deg)` : 'rotate(0deg)',
        },
        to: {
            transform: `rotate(${spinTo}deg)`,
        },
        loop: answer === null,
        onRest: () => {
            if (answer !== null) {
                onAnimationEnd();
            }
        },
        config: {
            easing: answer !== null ? easings.easeOutQuint : null,
            duration: 4000,
        },
    });

    const onClick = useCallback(() => {
        if (!disabled) {
            onAnswer();
            setSpinTo(1080);
        }
    }, [disabled, choices]);

    useEffect(() => {
        if (answerDegree === null) {
            return;
        }
        setSpinTo(1080 + answerDegree);
    }, [answerDegree]);

    const mediumImage = getImageAtSize(image, 'medium');
    const { url: mediumImageUrl = null } = mediumImage || {};
    const mediumLogo = getImageAtSize(logo, 'medium');
    const { url: mediumLogoUrl = null } = mediumLogo || {};

    return (
        <div
            className={classNames([
                styles.container,
                themeClassName,
                {
                    [styles[id]]: id !== null,
                    [styles.disabled]: disabled,
                    [className]: className !== null,
                },
            ])}
        >
            {body !== null ? <div className={styles.body}>{body}</div> : null}
            <div className={styles.wrapper}>
                <div className={styles.inner}>
                    <WheelIndicator className={styles.indicator} />
                    <div className={styles.imageContainer}>
                        <animated.div style={{ ...spring }} className={styles.rotate}>
                            {mediumImageUrl !== null ? (
                                <>
                                    <div className={styles.wheelImageBackground} />
                                    <img
                                        src={mediumImageUrl}
                                        alt={body}
                                        className={styles.wheelImage}
                                    />
                                </>
                            ) : null}
                            <div className={styles.choicesContainer}>
                                {(choices || []).map(({ choiceId = null, label = null }, ind) => (
                                    <div
                                        className={styles.choice}
                                        key={`wheel-choice-${choiceId}`}
                                        style={{
                                            transform: `translateX(-50%) rotate(${
                                                (360 / choices.length) * ind +
                                                360 / choices.length / 2
                                            }deg) `,
                                        }}
                                    >
                                        <span className={styles.choiceLabel}>
                                            {label !== null ? label : null}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </animated.div>
                        {mediumLogoUrl !== null ? (
                            <img src={mediumLogoUrl} alt="" className={styles.centerImage} />
                        ) : null}
                    </div>
                    <Button onClick={onClick} className={styles.spinButton}>
                        <CurvedArrow direction="right" className={styles.buttonArrow} />
                        <span className={styles.buttonLabel}>
                            <FormattedMessage defaultMessage="Spin" description="Button label" />
                        </span>
                        <CurvedArrow direction="left" className={styles.buttonArrow} />
                    </Button>
                </div>
            </div>
        </div>
    );
}

WheelQuestion.propTypes = propTypes;
WheelQuestion.defaultProps = defaultProps;

export default WheelQuestion;
